import React, { useState, useEffect } from 'react';
import { X, Plus, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/apiService';

// Helper function to filter out non-selectable categories (fallback categories)
const getSelectableCategories = (categories) => {
  return categories.filter(cat => !cat.startsWith('⚠️'));
};

const CATEGORIES = [
  'General',
  'Fruits & Vegetables',
  'Meat & Seafood',
  'Dairy & Eggs',
  'Bakery',
  'Pantry',
  'Frozen',
  'Beverages',
  'Snacks',
  'Health & Beauty',
  'Household',
  'Other'
];

// Smart category mapping for common items
const ITEM_CATEGORY_MAP = {
  // Fruits & Vegetables
  'apple': 'Fruits & Vegetables',
  'apples': 'Fruits & Vegetables',
  'banana': 'Fruits & Vegetables',
  'bananas': 'Fruits & Vegetables',
  'tomato': 'Fruits & Vegetables',
  'tomatoes': 'Fruits & Vegetables',
  'onion': 'Fruits & Vegetables',
  'onions': 'Fruits & Vegetables',
  'potato': 'Fruits & Vegetables',
  'potatoes': 'Fruits & Vegetables',
  'carrot': 'Fruits & Vegetables',
  'carrots': 'Fruits & Vegetables',
  'lettuce': 'Fruits & Vegetables',
  'spinach': 'Fruits & Vegetables',
  'cucumber': 'Fruits & Vegetables',
  'broccoli': 'Fruits & Vegetables',
  'orange': 'Fruits & Vegetables',
  'oranges': 'Fruits & Vegetables',
  
  // Dairy & Eggs
  'milk': 'Dairy & Eggs',
  'cheese': 'Dairy & Eggs',
  'butter': 'Dairy & Eggs',
  'yogurt': 'Dairy & Eggs',
  'eggs': 'Dairy & Eggs',
  'cream': 'Dairy & Eggs',
  
  // Meat & Seafood
  'chicken': 'Meat & Seafood',
  'beef': 'Meat & Seafood',
  'pork': 'Meat & Seafood',
  'fish': 'Meat & Seafood',
  'salmon': 'Meat & Seafood',
  'shrimp': 'Meat & Seafood',
  'turkey': 'Meat & Seafood',
  
  // Bakery
  'bread': 'Bakery',
  'bagel': 'Bakery',
  'bagels': 'Bakery',
  'muffin': 'Bakery',
  'muffins': 'Bakery',
  'croissant': 'Bakery',
  
  // Pantry
  'rice': 'Pantry',
  'pasta': 'Pantry',
  'flour': 'Pantry',
  'sugar': 'Pantry',
  'salt': 'Pantry',
  'pepper': 'Pantry',
  'oil': 'Pantry',
  'vinegar': 'Pantry',
  'beans': 'Pantry',
  'lentils': 'Pantry',
  
  // Beverages
  'water': 'Beverages',
  'juice': 'Beverages',
  'coffee': 'Beverages',
  'tea': 'Beverages',
  'soda': 'Beverages',
  'beer': 'Beverages',
  'wine': 'Beverages',
  
  // Snacks
  'chips': 'Snacks',
  'cookies': 'Snacks',
  'crackers': 'Snacks',
  'nuts': 'Snacks',
  'chocolate': 'Snacks',
  
  // Frozen
  'ice cream': 'Frozen',
  'frozen vegetables': 'Frozen',
  'frozen fruit': 'Frozen',
  'frozen pizza': 'Frozen',
  
  // Health & Beauty
  'shampoo': 'Health & Beauty',
  'soap': 'Health & Beauty',
  'toothpaste': 'Health & Beauty',
  'deodorant': 'Health & Beauty',
  
  // Household
  'toilet paper': 'Household',
  'paper towels': 'Household',
  'detergent': 'Household',
  'dish soap': 'Household',
  'cleaning supplies': 'Household'
};

const AddItemModal = ({ onClose, onSubmit, existingItems = [] }) => {
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [category, setCategory] = useState('General');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const cats = await apiService.getUserCategories();
      setCategories(cats);
      // Set default category to first selectable category
      const selectableCategories = getSelectableCategories(cats);
      if (selectableCategories.length > 0) {
        setCategory(selectableCategories[0]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback to hardcoded categories if API fails
      setCategories(['General', 'Food', 'Household', 'Personal Care']);
      setCategory('General');
    }
  };

  // Get smart category suggestion based on item name
  const getSmartCategory = async (itemName) => {
    const lowerName = itemName.toLowerCase().trim();
    
    // First try to get learned category from backend
    try {
      const learnedData = await apiService.getLearnedCategory(itemName);
      if (learnedData.category) {
        return learnedData.category;
      }
    } catch (err) {
      console.log('Could not get learned category, falling back to local logic');
    }
    
    // Check for exact matches in predefined map
    if (ITEM_CATEGORY_MAP[lowerName]) {
      return ITEM_CATEGORY_MAP[lowerName];
    }
    
    // Check for partial matches
    for (const [key, cat] of Object.entries(ITEM_CATEGORY_MAP)) {
      if (lowerName.includes(key) || key.includes(lowerName)) {
        return cat;
      }
    }
    
    // Check user's previous choices from localStorage (legacy support)
    const userCategoryMap = JSON.parse(localStorage.getItem('userCategoryMap') || '{}');
    if (userCategoryMap[lowerName]) {
      return userCategoryMap[lowerName];
    }
    
    return 'General';
  };

  // Update category when name changes (now async)
  useEffect(() => {
    const updateCategory = async () => {
      if (name.trim()) {
        const suggestedCategory = await getSmartCategory(name);
        setCategory(suggestedCategory);
      }
    };
    
    updateCategory();
  }, [name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    setLoading(true);
    
    try {
      // Save user's category choice for future suggestions
      const userCategoryMap = JSON.parse(localStorage.getItem('userCategoryMap') || '{}');
      userCategoryMap[name.toLowerCase().trim()] = category;
      localStorage.setItem('userCategoryMap', JSON.stringify(userCategoryMap));

      await onSubmit({
        name: name.trim(),
        quantity: parseInt(quantity),
        category,
        notes: notes.trim()
      });

      // Close modal after successful submission
      onClose();
      
    } catch (err) {
      console.error('Error adding item:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <Package size={24} />
            <h2>{t('addNewItem') || t('addItem', 'Add New Item')}</h2>
          </div>
          <button onClick={onClose} className="modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-row">
              <div className="form-group flex-1">
                <label htmlFor="itemName">{t('itemName')} *</label>
                <input
                  id="itemName"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('enterItemName', 'Enter item name...')}
                  className="form-input"
                  autoFocus
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label htmlFor="itemQuantity">{t('quantity')}</label>
                <input
                  id="itemQuantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  max="999"
                  className="form-input quantity-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="itemCategory">{t('category')}</label>
              <select
                id="itemCategory"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="form-select"
              >
                {getSelectableCategories(categories).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="itemNotes">{t('notes')} ({t('optional', 'Optional')})</label>
              <textarea
                id="itemNotes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('enterNotes', 'Enter notes...')}
                className="form-textarea"
                rows={2}
                maxLength={300}
              />
            </div>

            <div className="modal-actions">
              <button 
                type="button" 
                onClick={onClose} 
                className="btn-secondary"
                disabled={loading}
              >
                {t('done', 'Done')}
              </button>
              <button 
                type="submit" 
                className="btn-primary"
                disabled={!name.trim() || loading}
              >
                {loading ? t('adding', 'Adding...') : t('addItem')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddItemModal;
